import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { t } from "@/i18n";

/* =============================================================================
 * Shared nav + footer for all public pages. English-only, no EN/FR switcher.
 * ========================================================================== */

export const BRAND = "#3B82F6";

const LINKS = [
  { to: "/features", label: "Features", match: "/features" },
  { to: "/pricing", label: "Pricing", match: "/pricing" },
  {
    to: "/alphabet",
    label: "Practice Alphabet",
    match: "/Alphabet",
  },
  { to: "/#faq", label: "FAQ", match: "#faq" },
];

const GLASS =
  "rounded-full bg-white/70 backdrop-blur-xl border border-slate-200/60 shadow-sm";

export function PublicNav() {
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname, hash]);

  const isActive = (l) =>
    l.match.startsWith("#") ? hash === l.match : pathname === l.match;

  return (
    <>
      {/* ---------- Mobile (< md): fixed full-width bar ---------- */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-16 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl">
        <div className="h-16 px-4 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="flex items-center gap-2.5 shrink-0"
            aria-label={t("publicchrome.harf_home")}
          >
            <span
              className="w-9 h-9 rounded-xl grid place-items-center text-white text-xl font-bold arabic"
              style={{ background: BRAND }}
            >
              ع
            </span>
          </Link>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/auth"
              className="px-3 py-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition"
            >
              {t("publicchrome.log_in")}
            </Link>
            <Link
              to="/auth?mode=signup"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white shadow-sm transition active:translate-y-px"
              style={{ background: BRAND }}
            >
              {t("publicchrome.sign_up")}
            </Link>
            <button
              onClick={() => setOpen((o) => !o)}
              className="p-1.5 rounded-lg text-slate-700 hover:bg-slate-100 transition"
              aria-label={open ? t("publicchrome.close_menu") : t("publicchrome.open_menu")}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="absolute top-16 inset-x-0 border-b border-slate-200 bg-white p-3 shadow-xl">
            <nav className="grid gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`px-4 py-3 rounded-xl text-[15px] font-medium transition ${
                    isActive(l)
                      ? "bg-blue-50 text-blue-600"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* ---------- Desktop (md+): floating glass pills ---------- */}
      <header className="hidden md:block fixed top-0 inset-x-0 z-40 pointer-events-none">
        <style>{`
          .harf-nav-pill::-webkit-scrollbar { display: none; }
          .harf-nav-pill { scrollbar-width: none; -ms-overflow-style: none; }
        `}</style>
        <div className="max-w-7xl mx-auto px-6 pt-4 flex items-center justify-between gap-3 min-w-0">
          <Link
            to="/"
            className={`pointer-events-auto flex items-center gap-2 shrink-0 px-3.5 py-2 hover:brightness-95 transition ${GLASS}`}
            aria-label={t("publicchrome.harf_home")}
          >
            <span
              className="w-7 h-7 rounded-lg grid place-items-center text-white text-sm font-bold arabic"
              style={{ background: BRAND }}
            >
              ع
            </span>
            <span className="font-head text-base font-extrabold text-slate-900 pr-1">
              {t("navbar.harf")}
            </span>
          </Link>

          <nav
            aria-label={t("publicchrome.main_navigation")}
            className={`harf-nav-pill pointer-events-auto flex items-center gap-1 px-1.5 py-1.5 min-w-0 overflow-x-auto ${GLASS}`}
          >
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-[14px] font-semibold whitespace-nowrap transition ${
                  isActive(l)
                    ? "bg-blue-50 text-blue-600"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="pointer-events-auto flex items-center gap-2 shrink-0">
            <div className={`flex items-center gap-1 px-1.5 py-1.5 ${GLASS}`}>
              <Link
                to="/auth"
                className="px-3.5 py-1.5 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
              >
                {t("publicchrome.log_in")}
              </Link>
            </div>
            <Link
              to="/auth?mode=signup"
              className={`px-4 py-2 rounded-full text-sm font-semibold text-white shadow-sm hover:opacity-90 active:translate-y-px transition ${GLASS}`}
              style={{ background: BRAND, borderColor: BRAND }}
            >
              {t("publicchrome.sign_up")}
            </Link>
          </div>
        </div>
      </header>
    </>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5">
            <span
              className="w-8 h-8 rounded-lg grid place-items-center text-white font-bold arabic"
              style={{ background: BRAND }}
            >
              ع
            </span>
            <span className="font-head font-extrabold text-slate-900">
              {t("navbar.harf")}
            </span>
          </Link>

          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-slate-500">
            <Link to="/features" className="hover:text-slate-900">
              {t("publicchrome.features")}
            </Link>
            <Link to="/pricing" className="hover:text-slate-900">
              {t("publicchrome.pricing")}
            </Link>
            <Link to="/alphabet" className="hover:text-slate-900">
              {t("publicchrome.alphabet")}
            </Link>
            <Link to="/legal/mentions" className="hover:text-slate-900">
              {t("publicchrome.legal_notice")}
            </Link>
            <Link to="/legal/cgu" className="hover:text-slate-900">
              {t("publicchrome.terms")}
            </Link>
            <Link to="/legal/confidentialite" className="hover:text-slate-900">
              {t("publicchrome.privacy")}
            </Link>
          </nav>
        </div>
        <p className="mt-8 text-xs text-slate-400 text-center sm:text-left">
          © {new Date().getFullYear()} {t("publicchrome.harf_learn_to_read_write_and")}
        </p>
      </div>
    </footer>
  );
}

/** Shared white wrapper: forces the light theme, adds the top padding every
    page needs to clear the fixed nav (h-16 mobile / floating pill desktop). */
export function PublicShell({ children, className = "" }) {
  return (
    <div
      className={`min-h-screen bg-white text-slate-900 pt-16 md:pt-24 ${className}`}
    >
      <PublicNav />
      {children}
      <PublicFooter />
    </div>
  );
}
